import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AssessmentTypeEnum } from '@haleo-frontend/data-access/enums';
import { Assessment, Paginated } from '@haleo-frontend/data-access/models';
import { AbstractComponent, EnvironmentUtils, TrackingUtils } from '@haleo-frontend/utils';
import { Moment } from 'moment';
import { empty, Observable, throwError } from 'rxjs';
import { catchError, expand, map, take, takeUntil } from 'rxjs/operators';
import { AssessmentService } from './assessment.service';
import { ClientService } from './client.service';

@Injectable({
    providedIn: 'root'
})
export class SleepDiaryService extends AbstractComponent {

    private isWorkingShift: boolean | undefined;
    workingShift: Assessment | null | undefined;

    constructor(private http: HttpClient,
                private router: Router,
                private clientService: ClientService,
                private assessmentService: AssessmentService) {
        super();
    }

    createSleepDiary(date: string, type: string): Observable<Assessment> {
        return this.http.post<Assessment>(EnvironmentUtils.env.api + 'assessments', {version: date, type});
    }

    getSelfGuidedSleepDiaryForDate(date: string): Observable<Assessment | null> {
        return this.http.get<Paginated[]>(EnvironmentUtils.env.api + `assessments?type=${AssessmentTypeEnum.sleepDiarySelfGuided}&versionFrom=${date}&versionTo=${date}&embed=questions,answers`)
            .pipe(map((data: any) => {
                if (data.length > 0) {
                    return data[0];
                } else {
                    return null;
                }
            }));
    }

    getCompletedForDate(date: string): Observable<Assessment[]> {
        return this.http.get<Paginated[]>(EnvironmentUtils.env.api + `assessments?type=${AssessmentTypeEnum.sleepDiaryAll}&versionFrom=${date}&versionTo=${date}&embed=questions,answers&completed=1`)
            .pipe(map((data: any) => data.data));
    }

    getCompletedForRange(start: string, end: string): Observable<Assessment[]> {
        return this.http.get<Paginated[]>(EnvironmentUtils.env.api + `assessments?type=${AssessmentTypeEnum.sleepDiaryAll}&versionFrom=${start}&versionTo=${end}&completed=1`)
            .pipe(map((data: any) => data.data));
    }

    getAllForDate(date: string): Observable<Assessment[]> {
        return this.http.get<Paginated[]>(EnvironmentUtils.env.api + `assessments?type=${AssessmentTypeEnum.sleepDiaryAll}&versionFrom=${date}&versionTo=${date}&embed=questions,answers`)
            .pipe(map((data: any) => data.data));
    }

    getForDate(date: string): Observable<Assessment> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + `assessments?type=${AssessmentTypeEnum.sleepDiaryAll}&versionFrom=${date}&versionTo=${date}&embed=questions,answers`)
            .pipe(map(data => {
                if (data.data.length > 0) {
                    return data.data[0];
                } else {
                    return null;
                }
            }));
    }

    paginatedData(dayRange: any, order = 'desc'): Observable<Assessment[]> {

        return this.getCompletedForDateRange(dayRange.start.format('YYYY-MM-DD'), dayRange.end.format('YYYY-MM-DD'), 1, order)
            .pipe(expand(data => {

                if (data.meta.current_page < data.meta.last_page) {
                    return this.getCompletedForDateRange(dayRange.start.format('YYYY-MM-DD'), dayRange.end.format('YYYY-MM-DD'), ++data.meta.current_page, order);
                } else {
                    return empty();
                }
            }), map((data: any) => data.data));
    }

    private getCompletedForDateRange(start: string, end: string, page: number, order: string): Observable<Paginated> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + `assessments?type=${AssessmentTypeEnum.sleepDiaryAll}&versionFrom=${start}&versionTo=${end}&embed=questions,answers&completed=1&page=${page}&order=${order}`);
    }

    async startSleepDiary(day: Moment) {

        this.clientService.get()
            .pipe(takeUntil(this.destroy))
            .subscribe(client => {
                this.isWorkingShift = client.sleepDiaryType === AssessmentTypeEnum.sleepDiaryShiftWork;
            });

        this.workingShift = await this.assessmentService.getWorkingShiftByDate(day.format('YYYY-MM-DD'))
            .pipe(take(1), takeUntil(this.destroy)).toPromise();

        if (!this.isWorkingShift || this.workingShift) {

            const type = this.isWorkingShift ? AssessmentTypeEnum.sleepDiaryShiftWork : AssessmentTypeEnum.sleepDiary;
            this.createSleepDiary(day.format('YYYY-MM-DD'), type)
                .pipe(catchError(error => {

                        // A sleep diary exist but is not completed
                        if (error.status === 403) {
                            return this.getForDate(day.format('YYYY-MM-DD'));
                        } else {
                            return throwError(error);
                        }
                    }),
                    takeUntil(this.destroy))
                .subscribe(sleepDiary => {
                    this.router.navigate(['/assessment/' + sleepDiary.uuid], {queryParams: {async: 1}});
                    TrackingUtils.trackEvent('sleep_diary_created');
                });
        } else {
            this.router.navigate(['/sleep-diary/work-shift', day.format('YYYY-MM-DD')]);
        }
    }
}
